import React from 'react'
import { cn } from '@/utils/cn'
import { Tooltip } from './Tooltip'
import { TrendingUp, TrendingDown, Minus, Info } from 'lucide-react'

export interface MetricThresholds {
  excellent: number
  good: number
  warning: number
}

export interface MetricCardProps {
  title: string
  value: number | string
  unit?: string
  description: string
  benchmark?: string
  thresholds?: MetricThresholds
  format?: 'percentage' | 'currency' | 'ratio' | 'decimal' | 'integer'
  trend?: 'up' | 'down' | 'neutral'
  trendValue?: number
  loading?: boolean
  className?: string
}

function formatValue(value: number | string, format?: string, unit?: string): string {
  if (typeof value === 'string') return value

  switch (format) {
    case 'percentage':
      return `${value.toFixed(2)}%`
    case 'currency':
      return `$${value.toLocaleString()}`
    case 'ratio':
      return value.toFixed(2)
    case 'decimal':
      return value.toFixed(2)
    case 'integer':
      return Math.round(value).toString()
    default:
      return unit ? `${value.toFixed(2)}${unit}` : value.toString()
  }
}

function getMetricColor(value: number, thresholds?: MetricThresholds, format?: string): string {
  if (!thresholds) return 'gray'

  // For percentage metrics, convert to decimal for comparison
  const compareValue = format === 'percentage' ? value : value

  if (compareValue >= thresholds.excellent) return 'green'
  if (compareValue >= thresholds.good) return 'blue'
  if (compareValue >= thresholds.warning) return 'yellow'
  return 'red'
}

function getColorClasses(color: string): { bg: string; text: string; border: string } {
  const colorMap = {
    green: { bg: 'bg-green-50', text: 'text-green-900', border: 'border-green-200' },
    blue: { bg: 'bg-blue-50', text: 'text-blue-900', border: 'border-blue-200' },
    yellow: { bg: 'bg-yellow-50', text: 'text-yellow-900', border: 'border-yellow-200' },
    red: { bg: 'bg-red-50', text: 'text-red-900', border: 'border-red-200' },
    gray: { bg: 'bg-gray-50', text: 'text-gray-900', border: 'border-gray-200' }
  }
  return colorMap[color as keyof typeof colorMap] || colorMap.gray
}

function getTrendIcon(trend?: 'up' | 'down' | 'neutral') {
  switch (trend) {
    case 'up':
      return <TrendingUp className="w-4 h-4 text-green-600" />
    case 'down':
      return <TrendingDown className="w-4 h-4 text-red-600" />
    case 'neutral':
      return <Minus className="w-4 h-4 text-gray-600" />
    default:
      return null
  }
}

export function MetricCard({
  title,
  value,
  unit,
  description,
  benchmark,
  thresholds,
  format = 'decimal',
  trend,
  trendValue,
  loading = false,
  className
}: MetricCardProps) {
  const numericValue = typeof value === 'number' ? value : 0
  const color = getMetricColor(numericValue, thresholds, format)
  const colorClasses = getColorClasses(color)
  const formattedValue = formatValue(value, format, unit)

  const tooltipContent = [
    description,
    benchmark && `Industry Benchmark: ${benchmark}`,
    thresholds && `Excellent: ${thresholds.excellent}+ • Good: ${thresholds.good}+ • Warning: ${thresholds.warning}+`
  ].filter(Boolean).join('\n\n')

  if (loading) {
    return (
      <div className={cn(
        "bg-white rounded-lg border p-6 animate-pulse",
        className
      )}>
        <div className="flex items-center justify-between mb-4">
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          <div className="h-4 w-4 bg-gray-200 rounded-full"></div>
        </div>
        <div className="h-8 bg-gray-200 rounded w-3/4 mb-2"></div>
        <div className="h-3 bg-gray-200 rounded w-1/3"></div>
      </div>
    )
  }

  return (
    <div className={cn(
      "bg-white rounded-lg border p-6 transition-all duration-200 hover:shadow-md",
      colorClasses.border,
      className
    )}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-gray-700">{title}</h3>
        <Tooltip content={tooltipContent} position="left">
          <Info className="w-4 h-4 text-gray-400 hover:text-primary-600 cursor-help" />
        </Tooltip>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className={cn(
            "text-2xl font-bold",
            colorClasses.text
          )}>
            {formattedValue}
          </div>
          {trend && getTrendIcon(trend)}
        </div>

        {trendValue !== undefined && (
          <div className="flex items-center text-sm text-gray-600">
            <span className={cn(
              "font-medium",
              trend === 'up' ? 'text-green-600' :
              trend === 'down' ? 'text-red-600' : 'text-gray-600'
            )}>
              {trend === 'up' ? '+' : trend === 'down' ? '-' : ''}
              {Math.abs(trendValue).toFixed(2)}%
            </span>
            <span className="ml-1">vs last period</span>
          </div>
        )}

        {benchmark && (
          <div className="text-xs text-gray-500 border-t pt-2">
            <span className="font-medium">Benchmark:</span> {benchmark}
          </div>
        )}
      </div>
    </div>
  )
}